import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { supabase } from '../lib/supabase';

const TOKEN_TIMEOUT_MS = 10000;
const LIVEKIT_CONNECT_TIMEOUT_MS = 15000;
const RECENT_SPEAKER_WINDOW_MS = 6000;

function getDisplayName(user) {
  const meta = user?.user_metadata || {};
  return meta.display_name || meta.full_name || meta.username || user?.email || 'Unknown user';
}

function logVoiceEvent(event, details) {
  if (details) {
    console.info('[room-voice]', event, details);
    return;
  }

  console.info('[room-voice]', event);
}

async function withTimeout(promise, timeoutMs, message) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
}

function validateLiveKitUrl(url) {
  if (typeof url !== 'string' || url.length === 0) {
    throw new Error('LiveKit URL is missing from voice-token.');
  }

  if (!/^wss?:\/\//i.test(url)) {
    throw new Error('LiveKit URL must start with ws:// or wss://.');
  }
}

function ensureBrowserVoiceSupport() {
  if (!window.isSecureContext) {
    throw new Error('Voice chat requires a secure HTTPS connection.');
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('This browser does not support microphone access for voice chat.');
  }
}

function mapConnectionState(state) {
  switch (state) {
    case 'connected':
      return 'connected';
    case 'connecting':
      return 'connecting';
    case 'reconnecting':
    case 'signalReconnecting':
      return 'reconnecting';
    case 'disconnected':
    default:
      return 'idle';
  }
}

function supportsAudioOutputSelection() {
  return typeof HTMLMediaElement !== 'undefined' && typeof HTMLMediaElement.prototype.setSinkId === 'function';
}

function buildCaptureOptions(deviceId, noiseSuppressionEnabled) {
  return {
    autoGainControl: true,
    echoCancellation: true,
    noiseSuppression: noiseSuppressionEnabled,
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  };
}

function toDeviceOption(device, index, fallbackLabel) {
  return {
    deviceId: device.deviceId,
    label: device.label || `${fallbackLabel} ${index + 1}`,
  };
}

function getParticipantState(participant, isSelf = false, recentSpeakers = new Map()) {
  const publication = participant.getTrackPublication(Track.Source.Microphone);
  const lastSpokeAt = recentSpeakers.get(participant.identity) || null;
  const recentlySpoke = Boolean(lastSpokeAt && Date.now() - lastSpokeAt < RECENT_SPEAKER_WINDOW_MS);

  return {
    userId: participant.identity,
    displayName: participant.name || participant.identity,
    hasMicrophone: Boolean(publication),
    isMuted: publication ? publication.isMuted : true,
    isSelf,
    isSpeaking: Boolean(participant.isSpeaking),
    recentlySpoke,
  };
}

export function useRoomVoice({ roomId, user, enabled = true }) {
  const roomRef = useRef(null);
  const audioElementsRef = useRef(new Map());
  const recentSpeakersRef = useRef(new Map());
  const manualDisconnectRef = useRef(false);
  const presenceChannelRef = useRef(null);
  const voiceJoinedAtRef = useRef(null);
  const lastPresencePayloadRef = useRef('');

  const [participants, setParticipants] = useState([]);
  const [joined, setJoined] = useState(false);
  const [selfMuted, setSelfMuted] = useState(false);
  const [isMicInitializing, setIsMicInitializing] = useState(false);
  const [isSwitchingDevice, setIsSwitchingDevice] = useState(false);
  const [error, setError] = useState('');
  const [connectionState, setConnectionState] = useState('idle');
  const [statusDetail, setStatusDetail] = useState('');
  const [needsAudioResume, setNeedsAudioResume] = useState(false);
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [selectedAudioInputId, setSelectedAudioInputId] = useState('');
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState('');
  const [noiseSuppressionEnabled, setNoiseSuppressionEnabled] = useState(true);
  const [canPublishAudio, setCanPublishAudio] = useState(true);
  const [publishRestrictionReason, setPublishRestrictionReason] = useState('');
  const [isRoomOwner, setIsRoomOwner] = useState(false);
  const [isVoiceLocked, setIsVoiceLocked] = useState(false);

  const userId = user?.id || null;
  const displayName = useMemo(() => getDisplayName(user), [user]);
  const canSelectAudioOutput = useMemo(() => supportsAudioOutputSelection(), []);

  const clearAudioElements = useCallback(() => {
    audioElementsRef.current.forEach((element) => {
      try {
        element.remove();
      } catch (_) {}
    });
    audioElementsRef.current.clear();
  }, []);

  const detachAudioTrack = useCallback((track) => {
    const existing = audioElementsRef.current.get(track.sid);
    if (existing) {
      try {
        track.detach(existing);
      } catch (_) {}
      existing.remove();
      audioElementsRef.current.delete(track.sid);
      return;
    }

    try {
      track.detach().forEach((element) => element.remove());
    } catch (_) {}
  }, []);

  const applyAudioOutputDevice = useCallback(async (deviceId) => {
    if (!deviceId || !canSelectAudioOutput) return;

    const tasks = [];
    audioElementsRef.current.forEach((element) => {
      if (typeof element.setSinkId === 'function') {
        tasks.push(element.setSinkId(deviceId));
      }
    });

    await Promise.all(tasks);
  }, [canSelectAudioOutput]);

  const attachAudioTrack = useCallback(async (track) => {
    if (track.kind !== Track.Kind.Audio) return;
    detachAudioTrack(track);

    const element = track.attach();
    element.autoplay = true;
    element.playsInline = true;
    element.style.display = 'none';
    document.body.appendChild(element);
    audioElementsRef.current.set(track.sid, element);

    if (selectedAudioOutputId && typeof element.setSinkId === 'function') {
      try {
        await element.setSinkId(selectedAudioOutputId);
      } catch (err) {
        console.warn('Unable to assign audio output device', err);
      }
    }
  }, [detachAudioTrack, selectedAudioOutputId]);

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const nextInputs = devices
        .filter((device) => device.kind === 'audioinput' && device.deviceId)
        .map((device, index) => toDeviceOption(device, index, 'Microphone'));
      const nextOutputs = devices
        .filter((device) => device.kind === 'audiooutput' && device.deviceId)
        .map((device, index) => toDeviceOption(device, index, 'Speaker'));

      setAudioInputDevices(nextInputs);
      setAudioOutputDevices(nextOutputs);

      const room = roomRef.current;
      const activeInputId = room?.getActiveDevice('audioinput') || '';
      const activeOutputId = room?.getActiveDevice('audiooutput') || '';

      setSelectedAudioInputId((current) => {
        if (activeInputId && nextInputs.some((device) => device.deviceId === activeInputId)) {
          return activeInputId;
        }
        if (current && nextInputs.some((device) => device.deviceId === current)) {
          return current;
        }
        return nextInputs[0]?.deviceId || '';
      });

      setSelectedAudioOutputId((current) => {
        if (activeOutputId && nextOutputs.some((device) => device.deviceId === activeOutputId)) {
          return activeOutputId;
        }
        if (current && nextOutputs.some((device) => device.deviceId === current)) {
          return current;
        }
        return nextOutputs[0]?.deviceId || '';
      });
    } catch (err) {
      console.warn('Unable to enumerate audio devices', err);
    }
  }, []);

  const closePresenceChannel = useCallback(async () => {
    const channel = presenceChannelRef.current;
    presenceChannelRef.current = null;
    lastPresencePayloadRef.current = '';

    if (!channel) return;

    try {
      await channel.untrack();
    } catch (_) {}

    supabase.removeChannel(channel);
  }, []);

  const ensurePresenceChannel = useCallback(async () => {
    if (!roomId || !userId) return null;
    if (presenceChannelRef.current) return presenceChannelRef.current;

    const channel = supabase.channel(`room-voice-presence:${roomId}`, {
      config: {
        presence: {
          key: userId,
        },
      },
    });

    presenceChannelRef.current = channel;

    await new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        resolve(channel);
      }, 5000);

      channel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          window.clearTimeout(timeoutId);
          resolve(channel);
          return;
        }

        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          window.clearTimeout(timeoutId);
          reject(new Error(`Voice presence channel failed: ${status.toLowerCase()}.`));
        }
      });
    });

    return channel;
  }, [roomId, userId]);

  const syncParticipants = useCallback(() => {
    const room = roomRef.current;
    if (!room || !userId) {
      setParticipants([]);
      return;
    }

    const now = Date.now();
    room.remoteParticipants.forEach((participant) => {
      if (participant.isSpeaking) {
        recentSpeakersRef.current.set(participant.identity, now);
      }
    });

    if (room.localParticipant.isSpeaking) {
      recentSpeakersRef.current.set(userId, now);
    }

    recentSpeakersRef.current.forEach((lastSpokeAt, participantId) => {
      if (now - lastSpokeAt > RECENT_SPEAKER_WINDOW_MS) {
        recentSpeakersRef.current.delete(participantId);
      }
    });

    const next = [
      {
        ...getParticipantState(room.localParticipant, true, recentSpeakersRef.current),
        userId,
        displayName,
      },
    ];

    room.remoteParticipants.forEach((participant) => {
      next.push(getParticipantState(participant, false, recentSpeakersRef.current));
    });

    next.sort((a, b) => {
      if (a.isSelf && !b.isSelf) return -1;
      if (!a.isSelf && b.isSelf) return 1;
      if (a.isSpeaking !== b.isSpeaking) return a.isSpeaking ? -1 : 1;
      if (a.recentlySpoke !== b.recentlySpoke) return a.recentlySpoke ? -1 : 1;
      if (a.isMuted !== b.isMuted) return a.isMuted ? 1 : -1;
      return a.displayName.localeCompare(b.displayName);
    });

    setParticipants(next);
    setSelfMuted(next[0]?.isMuted ?? false);
  }, [displayName, userId]);

  const teardownRoom = useCallback(async ({ manual = true } = {}) => {
    const room = roomRef.current;
    roomRef.current = null;
    manualDisconnectRef.current = manual;
    recentSpeakersRef.current.clear();
    voiceJoinedAtRef.current = null;

    clearAudioElements();
    await closePresenceChannel();

    if (room) {
      room.removeAllListeners();
      try {
        await room.disconnect();
      } catch (_) {}
    }

    setParticipants([]);
    setJoined(false);
    setSelfMuted(false);
    setConnectionState('idle');
    setStatusDetail('');
    setNeedsAudioResume(false);
    setCanPublishAudio(true);
    setPublishRestrictionReason('');
    setIsRoomOwner(false);
    setIsVoiceLocked(false);
  }, [clearAudioElements, closePresenceChannel]);

  const registerRoomListeners = useCallback((room) => {
    room
      .on(RoomEvent.ConnectionStateChanged, (state) => {
        const nextState = mapConnectionState(state);
        setConnectionState(nextState);
        if (nextState === 'connecting') {
          setStatusDetail('Connecting to room voice…');
        }
        logVoiceEvent('connection_state_changed', { roomId, state });
      })
      .on(RoomEvent.Reconnecting, () => {
        setConnectionState('reconnecting');
        setStatusDetail('Connection interrupted. Rejoining room voice…');
        logVoiceEvent('reconnecting', { roomId });
      })
      .on(RoomEvent.Reconnected, () => {
        setConnectionState('connected');
        setStatusDetail('Connection restored.');
        syncParticipants();
        logVoiceEvent('reconnected', { roomId });
        window.setTimeout(() => {
          setStatusDetail((current) => (current === 'Connection restored.' ? '' : current));
        }, 2500);
      })
      .on(RoomEvent.MediaDevicesChanged, refreshDevices)
      .on(RoomEvent.ActiveDeviceChanged, (kind, deviceId) => {
        if (kind === 'audioinput') {
          setSelectedAudioInputId(deviceId);
        }
        if (kind === 'audiooutput') {
          setSelectedAudioOutputId(deviceId);
        }
      })
      .on(RoomEvent.ParticipantConnected, syncParticipants)
      .on(RoomEvent.ParticipantDisconnected, syncParticipants)
      .on(RoomEvent.TrackSubscribed, (track) => {
        attachAudioTrack(track);
        syncParticipants();
      })
      .on(RoomEvent.TrackUnsubscribed, (track) => {
        detachAudioTrack(track);
        syncParticipants();
      })
      .on(RoomEvent.TrackMuted, syncParticipants)
      .on(RoomEvent.TrackUnmuted, syncParticipants)
      .on(RoomEvent.ActiveSpeakersChanged, syncParticipants)
      .on(RoomEvent.ParticipantPermissionsChanged, async (_prevPermissions, participant) => {
        if (!participant?.isLocal) return;

        const nextCanPublish = participant.permissions?.canPublish !== false;
        setCanPublishAudio(nextCanPublish);
        if (!nextCanPublish) {
          setPublishRestrictionReason((current) => current || 'A moderator restricted your microphone.');
          try {
            await room.localParticipant.setMicrophoneEnabled(false);
          } catch (_) {}
        } else {
          setPublishRestrictionReason('');
        }
        syncParticipants();
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        setNeedsAudioResume(!room.canPlaybackAudio);
      })
      .on(RoomEvent.MediaDevicesError, (mediaError) => {
        setError(mediaError?.message || 'Microphone device error.');
      })
      .on(RoomEvent.Disconnected, (reason) => {
        const wasManual = manualDisconnectRef.current;
        roomRef.current = null;
        voiceJoinedAtRef.current = null;
        clearAudioElements();
        closePresenceChannel();
        setParticipants([]);
        setJoined(false);
        setConnectionState('idle');
        setNeedsAudioResume(false);
        setCanPublishAudio(true);
        setPublishRestrictionReason('');
        setIsRoomOwner(false);
        setIsVoiceLocked(false);
        if (!wasManual) {
          setError(reason ? `Voice disconnected: ${String(reason).toLowerCase().replace(/_/g, ' ')}.` : 'Voice disconnected.');
          setStatusDetail('Room voice ended. You can try joining again.');
          logVoiceEvent('disconnected', { roomId, reason: String(reason || 'unknown') });
        }
        manualDisconnectRef.current = false;
      });
  }, [attachAudioTrack, clearAudioElements, closePresenceChannel, detachAudioTrack, refreshDevices, roomId, syncParticipants]);

  const joinVoice = useCallback(async () => {
    if (!enabled || !roomId || !userId || joined) return;

    manualDisconnectRef.current = false;
    setIsMicInitializing(true);
    setError('');
    setStatusDetail('Preparing microphone…');

    try {
      ensureBrowserVoiceSupport();
      await refreshDevices();

      const { data: sessionData } = await supabase.auth.refreshSession();
      if (!sessionData.session) {
        throw new Error('Your session has expired. Please sign in again to use voice chat.');
      }

      const { data, error: tokenError } = await withTimeout(
        supabase.functions.invoke('voice-token', {
          body: { chatId: roomId },
        }),
        TOKEN_TIMEOUT_MS,
        'voice-token request timed out.',
      );

      if (tokenError) throw tokenError;
      if (!data?.livekitUrl || !data?.token) {
        throw new Error('Voice token response is incomplete.');
      }
      validateLiveKitUrl(data.livekitUrl);
      setCanPublishAudio(data?.canPublish !== false);
      setPublishRestrictionReason(data?.publishRestrictionReason || '');
      setIsRoomOwner(Boolean(data?.isRoomOwner));
      setIsVoiceLocked(Boolean(data?.isVoiceLocked));

      const room = new Room({
        adaptiveStream: false,
        dynacast: true,
      });
      roomRef.current = room;
      registerRoomListeners(room);

      await withTimeout(
        room.connect(data.livekitUrl, data.token),
        LIVEKIT_CONNECT_TIMEOUT_MS,
        'LiveKit connection timed out.',
      );

      if (data?.canPublish !== false) {
        await room.localParticipant.setMicrophoneEnabled(
          true,
          buildCaptureOptions(selectedAudioInputId, noiseSuppressionEnabled),
        );
      }

      voiceJoinedAtRef.current = new Date().toISOString();
      setJoined(true);
      setConnectionState(mapConnectionState(room.state));
      setStatusDetail('');
      setNeedsAudioResume(!room.canPlaybackAudio);
      syncParticipants();
      await refreshDevices();

      if (selectedAudioOutputId && canSelectAudioOutput) {
        try {
          await room.switchActiveDevice('audiooutput', selectedAudioOutputId, true);
          await applyAudioOutputDevice(selectedAudioOutputId);
        } catch (err) {
          console.warn('Unable to apply selected audio output device', err);
        }
      }

      if (!room.canPlaybackAudio) {
        try {
          await room.startAudio();
          setNeedsAudioResume(false);
        } catch (_) {}
      }

      logVoiceEvent('joined', {
        roomId,
        noiseSuppressionEnabled,
        selectedAudioInputId: selectedAudioInputId || 'default',
        selectedAudioOutputId: selectedAudioOutputId || 'default',
        canPublish: data?.canPublish !== false,
      });
    } catch (err) {
      console.error('Unable to join LiveKit room', err);
      setError(err?.message || 'Unable to join room voice.');
      setStatusDetail('');
      await teardownRoom({ manual: true });
    } finally {
      setIsMicInitializing(false);
    }
  }, [
    applyAudioOutputDevice,
    canSelectAudioOutput,
    enabled,
    joined,
    noiseSuppressionEnabled,
    refreshDevices,
    registerRoomListeners,
    roomId,
    selectedAudioInputId,
    selectedAudioOutputId,
    syncParticipants,
    teardownRoom,
    userId,
  ]);

  const leaveVoice = useCallback(async () => {
    await teardownRoom({ manual: true });
  }, [teardownRoom]);

  const toggleMute = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    if (!canPublishAudio) {
      setError(publishRestrictionReason || 'Your microphone is currently restricted.');
      return;
    }

    try {
      await room.localParticipant.setMicrophoneEnabled(selfMuted);
      syncParticipants();
      logVoiceEvent(selfMuted ? 'microphone_unmuted' : 'microphone_muted', { roomId });
    } catch (err) {
      console.error('Unable to toggle microphone mute', err);
      setError('Unable to update microphone state.');
    }
  }, [canPublishAudio, publishRestrictionReason, roomId, selfMuted, syncParticipants]);

  const startAudioPlayback = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;

    try {
      await room.startAudio();
      setNeedsAudioResume(false);
    } catch (err) {
      console.error('Unable to resume LiveKit audio playback', err);
      setError('Audio playback is blocked. Try again.');
      setNeedsAudioResume(true);
    }
  }, []);

  const selectAudioInput = useCallback(async (deviceId) => {
    setSelectedAudioInputId(deviceId);
    if (!joined || !roomRef.current || !deviceId) return;

    setIsSwitchingDevice(true);
    setError('');

    try {
      const switched = await roomRef.current.switchActiveDevice('audioinput', deviceId, true);
      if (!switched) {
        throw new Error('Unable to switch microphone.');
      }
      syncParticipants();
      logVoiceEvent('audio_input_switched', { roomId, deviceId });
    } catch (err) {
      console.error('Unable to switch microphone device', err);
      setError(err?.message || 'Unable to switch microphone.');
    } finally {
      setIsSwitchingDevice(false);
    }
  }, [joined, roomId, syncParticipants]);

  const selectAudioOutput = useCallback(async (deviceId) => {
    setSelectedAudioOutputId(deviceId);
    if (!deviceId || !canSelectAudioOutput) return;

    setIsSwitchingDevice(true);
    setError('');

    try {
      if (roomRef.current) {
        const switched = await roomRef.current.switchActiveDevice('audiooutput', deviceId, true);
        if (!switched) {
          throw new Error('Unable to switch speaker.');
        }
      }

      await applyAudioOutputDevice(deviceId);
      logVoiceEvent('audio_output_switched', { roomId, deviceId });
    } catch (err) {
      console.error('Unable to switch speaker device', err);
      setError(err?.message || 'Unable to switch speaker.');
    } finally {
      setIsSwitchingDevice(false);
    }
  }, [applyAudioOutputDevice, canSelectAudioOutput, roomId]);

  useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  useEffect(() => {
    const syncPresence = async () => {
      if (!joined || !roomId || !userId) {
        await closePresenceChannel();
        return;
      }

      const selfParticipant = participants.find((participant) => participant.isSelf);
      const payload = {
        room_id: roomId,
        user_id: userId,
        display_name: displayName,
        joined_at: voiceJoinedAtRef.current || new Date().toISOString(),
        connection_state: connectionState,
        has_microphone: selfParticipant?.hasMicrophone ?? canPublishAudio,
        is_muted: selfParticipant?.isMuted ?? selfMuted,
        is_speaking: selfParticipant?.isSpeaking ?? false,
        recently_spoke: selfParticipant?.recentlySpoke ?? false,
      };
      const serializedPayload = JSON.stringify(payload);

      if (serializedPayload === lastPresencePayloadRef.current) {
        return;
      }

      try {
        const channel = await ensurePresenceChannel();
        if (!channel) return;
        await channel.track(payload);
        lastPresencePayloadRef.current = serializedPayload;
      } catch (err) {
        console.warn('Unable to sync room voice presence', err);
      }
    };

    syncPresence();
  }, [
    closePresenceChannel,
    connectionState,
    displayName,
    ensurePresenceChannel,
    joined,
    participants,
    roomId,
    canPublishAudio,
    selfMuted,
    userId,
  ]);

  useEffect(() => {
    if (!enabled) {
      teardownRoom({ manual: true });
    }
  }, [enabled, teardownRoom]);

  useEffect(() => {
    return () => {
      teardownRoom({ manual: true });
    };
  }, [teardownRoom]);

  const speakingCount = useMemo(
    () => participants.filter((participant) => participant.isSpeaking).length,
    [participants],
  );

  return {
    audioInputDevices,
    audioOutputDevices,
    canPublishAudio,
    canSelectAudioOutput,
    connectionState,
    error,
    isMicInitializing,
    isRoomOwner,
    isSwitchingDevice,
    isVoiceLocked,
    joined,
    needsAudioResume,
    noiseSuppressionEnabled,
    participants,
    publishRestrictionReason,
    selectedAudioInputId,
    selectedAudioOutputId,
    selfMuted,
    speakingCount,
    statusDetail,
    joinVoice,
    leaveVoice,
    refreshDevices,
    selectAudioInput,
    selectAudioOutput,
    setNoiseSuppressionEnabled,
    startAudioPlayback,
    toggleMute,
  };
}
